import { Component, ChangeDetectorRef, OnInit, OnDestroy, AfterViewInit, Input, ElementRef, HostListener, ViewChild, signal } from '@angular/core';
import { LoadingComponent } from '@/components/loading/loading.component';
import { FormControl, FormGroup, ReactiveFormsModule, Validators, FormArray } from '@angular/forms';
import { Subscription } from 'rxjs';
import { ApiService } from '@/apiConnection/ApiService';
import { Urls } from '@/apiConnection/urls';
import { CommonModule } from '@angular/common';
import { CustomInputComponent } from '@/components/custom-input/custom-input.component';
import { ActivatedRoute, Router, RouterModule } from "@angular/router";
import { DocumentSpecialMeasuresFormComponent } from '@/pages/documents/document-special-measures-form/document-special-measures-form.component';
import { AuthenticationService } from '@/apiConnection/authentication.service';
import { Utils } from '@/utils/utils';
import { CustomSelectComponent } from '@/components/custom-select/custom-select.component';
import { ClientFormComponent } from '@/pages/clients/client-form/client-form.component';
import { CustomModalComponent } from '@/components/custom-modal/custom-modal.component';
import { DropdownCoordinatorService } from '@/components/dropdown-coordinator.service';
import { OfflineQueueService } from '@/offline/offline-queue.service';
import { SyncService } from '@/offline/sync.service';
import { WriteQueueEntry } from '@/offline/db';
import { DocumentDeviceMonitorService } from '@/offline/document-device-monitor.service';
import { DocumentIdCache } from '@/utils/document-id-cache';
import { DecimalInputDirective } from '@/directives/decimal-input.directive';
import { ChangeCalculatorComponent } from '@/components/change-calculator/change-calculator.component';

@Component({
  imports: [
    LoadingComponent,
    DecimalInputDirective,
    ReactiveFormsModule,
    CommonModule,
    CustomInputComponent,
    CustomSelectComponent,
    RouterModule,
    DocumentSpecialMeasuresFormComponent,
    CustomModalComponent,
    ChangeCalculatorComponent,
  ],
  templateUrl: './ticket-form.page.html'
})
export class TicketFormPage implements OnInit, AfterViewInit, OnDestroy {
  public loading = signal(false);
  public saving = signal(false);
  public saveForm!: FormGroup;
  public showResults = false;
  public filteredProducts: any[] = [];
  public idEditMeasures = 0
  public editMeasures = false;
  public showProductCatalog = false;
  public showMixPayModal = false;
  /** null = calculadora cerrada; si no, si el guardado pendiente imprime ticket regalo. */
  public pendingChangePrintGift: boolean | null = null;
  public specialMeasures: { w: number, h: number, t: number } = { w: 1, h: 1, t: 1 }
  public dropdowns: any = {
    clients: [],
    users: [],
    products: [],
    tariffs: [],
    offices: [],
    ibans: [],
    tax_config: [],
  };

  public paymentMethods = [
    { id: 'efectivo', name: 'Efectivo' },
    { id: 'tarjeta', name: 'Tarjeta' },
    { id: 'mixto', name: 'Mixto' },
    { id: 'bizum', name: 'Bizum' },
  ];

  public get mixPayMethods() {
    return this.paymentMethods.filter(m => m.id !== 'mixto');
  }

  public clientFormComponent = ClientFormComponent

  // the ticket always prices against the company's ticket_tariff, never client/invoice tariffs
  public ticketTariffId: number | null = null;

  @Input() id: number | string = 0;

  // hidden items dropdown when click outside
  @ViewChild('mainSearch') searchContainer!: ElementRef;
  @HostListener('document:click', ['$event'])
  onClickOutside(event: Event) {
    if (this.searchContainer && !this.searchContainer.nativeElement.contains(event.target)) {
      this.showResults = false;
    }
  }

  public pendingSyncEntry: WriteQueueEntry | null = null;

  private openedSubscription?: Subscription;
  private resolvedSubscription?: Subscription;

  openProductSearch() {
    // stopPropagation() en otros dropdowns (custom-select) impide que este
    // onClickOutside se dispare, así que avisamos explícitamente al abrirnos
    this.coordinator.notifyOpened(this);
    this.showResults = true;
  }

  constructor(
    private apiService: ApiService,
    private cdr: ChangeDetectorRef,
    private authenticationService: AuthenticationService,
    private router: Router,
    private route: ActivatedRoute,
    private coordinator: DropdownCoordinatorService,
    private offlineQueue: OfflineQueueService,
    private syncService: SyncService,
    private documentDeviceMonitor: DocumentDeviceMonitorService,
  ) {
    // TODO: deprecated, change in the future 😅
    this.router.routeReuseStrategy.shouldReuseRoute = () => false;
  }

  ngOnInit(): void {
    this.route.params.subscribe(params => {
      this.id = params['id'] || 0;

      this.createForm();
      this.getDropdowns();

      if (this.id != 0) {
        this.getById(this.id);
      } else {
        this.getIdByCompany();
      }

      this.checkPendingSync();
    })

    // Si un custom-select se abre, cerramos el dropdown de búsqueda de productos
    this.openedSubscription = this.coordinator.opened$.subscribe(source => {
      if (source !== this && this.showResults) {
        this.showResults = false;
      }
    });

    // Si este ticket (temp id o pendiente de actualización) termina de sincronizarse
    // mientras el formulario sigue abierto, recargamos contra el id real ya asignado.
    this.resolvedSubscription = this.syncService.resolved$.subscribe(({ tempId, realId, entityType }) => {
      if (entityType === 'tickets' && tempId === Number(this.id)) {
        this.refresh(realId);
      }
    });
  }

  ngOnDestroy(): void {
    this.openedSubscription?.unsubscribe();
    this.resolvedSubscription?.unsubscribe();
  }

  private async checkPendingSync(): Promise<void> {
    this.pendingSyncEntry = await this.offlineQueue.findPendingForEntity('tickets', Number(this.id)) ?? null;
    this.cdr.detectChanges();
  }

  ngAfterViewInit(): void {
    if (this.id == 0) {
      setTimeout(() => this.searchContainer?.nativeElement?.focus(), 50);
    }
  }

  createForm() {
    this.saveForm = new FormGroup({
      id_by_company: new FormControl(null),
      year: new FormControl(new Date().getFullYear(), [Validators.required]),
      date: new FormControl(new Date().toISOString().split('T')[0], [Validators.required]),
      // Sin método de pago el cierre de caja no sabe dónde contar el ticket
      payment_method: new FormControl(null, [Validators.required]),
      mix_pay: new FormArray([]),
      tax_included: new FormControl(1),
      user_id: new FormControl(null),
      discount_absolute_bool: new FormControl(0),
      discount: new FormControl(0),
      notes: new FormControl(null),
      client_id: new FormControl(null),
      subtotal: new FormControl('0.00'),
      total: new FormControl('0.00'),
      // Un ticket nunca se guarda como borrador, y el backend exige al menos una línea.
      items: new FormArray([], [Validators.required]),
      office_id: new FormControl(null),
      iban: new FormControl(null),
      document_ref: new FormControl(null),
    });

    this.saveForm.valueChanges.subscribe(() => this.calculateTicket());

    this.saveForm.get('payment_method')?.valueChanges.subscribe((method) => {
      if (method === 'mixto') {
        if (this.mixPay.length === 0) {
          this.addMixPay();
          this.addMixPay();
        }
      } else {
        while (this.mixPay.length) {
          this.mixPay.removeAt(0);
        }
      }
    });
  }

  get items() { return this.saveForm.get('items') as FormArray; }
  get itemControls() { return this.items.controls; }

  get mixPay() { return this.saveForm.get('mix_pay') as FormArray; }
  get mixPayControls() { return this.mixPay.controls; }

  get isMixedPayment(): boolean {
    return this.saveForm.get('payment_method')?.value === 'mixto';
  }

  addMixPay(itemData: any = null) {
    this.mixPay.push(new FormGroup({
      type: new FormControl(itemData?.type || null, [Validators.required]),
      quantity: new FormControl(itemData?.quantity || 0, [Validators.required, Validators.min(0.01)]),
    }));
  }

  removeMixPay(index: number) {
    this.mixPay.removeAt(index);
  }

  openMixPayModal() {
    this.showMixPayModal = true;
  }

  closeMixPayModal() {
    this.showMixPayModal = false;
  }

  async openCashDrawer() {
    const serial = (navigator as any).serial;

    if (!serial) {
      Utils.notification.alert('Tu navegador no soporta Web Serial API (usa Chrome o Edge)');
      return;
    }

    let port: any;

    try {
      const knownPorts = await serial.getPorts();
      port = knownPorts[0] ?? await serial.requestPort();

      await port.open({ baudRate: 9600 });

      const writer = port.writable.getWriter();
      try {
        // ESC/POS drawer kick-out command: ESC p m t1 t2
        await writer.write(new Uint8Array([0x1B, 0x70, 0x00, 0x19, 0xFA]));
      } finally {
        writer.releaseLock();
      }
    } catch (error) {
      Utils.notification.alert(Utils.extractErrorMessage(error) || 'No se pudo abrir la caja');
    } finally {
      await port?.close();
    }
  }

  get isTaxIncluded(): boolean {
    return Number(this.saveForm.get('tax_included')?.value) === 1;
  }

  getPrice(product: any, tariffId: any = null): number {
    const choosedTariffId = tariffId ?? this.ticketTariffId;
    const tariff = this.dropdowns.tariffs?.find((t: any) => t.id == choosedTariffId);

    if (!tariff || tariff.tax) {
      return (product.price_ticket || 0) / 100;
    }

    try {
      const more_prices = typeof product.more_prices === 'string'
        ? JSON.parse(product.more_prices)
        : product.more_prices;

      const specificPrice = more_prices?.find((p: any) => p.id == choosedTariffId);
      return (specificPrice ? specificPrice.price : (product.price_ticket || 0)) / 100;
    } catch (e) {
      return (product.price_ticket || 0) / 100;
    }
  }

  getDropdowns() {
    const config = { users: {}, clients: {}, products: {}, companies: {}, offices: {} };
    this.apiService.getSelectors(Urls.urls.selectors, config).subscribe({
      next: (data: any) => {
        const company = data.companies[0];

        const ticketTariff = company.ticket_tariff ? [{ id: company.ticket_tariff, name: `Base (${company.ticket_tariff})`, tax: true }] : []

        const invoice_tariffs = JSON.parse(company.invoice_tariffs || '[]').map((tariff: any) => ({
          id: Number(tariff.id),
          name: `Tarifa ${tariff.id} (${tariff.quotient})`,
          tax: false
        }));

        this.dropdowns.tariffs = [
          ...ticketTariff,
          ...invoice_tariffs
        ];

        this.ticketTariffId = company.ticket_tariff ?? null;

        this.dropdowns.tax_config = company.tax_config ? JSON.parse(company.tax_config) : [];

        const ibans = company.iban
          ? (typeof company.iban === 'string'
            ? JSON.parse(company.iban)
            : company.iban)
          : [];

        this.dropdowns.ibans = ibans;

        const primaryIban = ibans.find((i: any) => i.is_primary);
        if (this.id == 0 && primaryIban) {
          this.saveForm.get('iban')?.setValue(primaryIban.iban);
        }

        const specialClientIds = company.special_clients
          ? (typeof company.special_clients === 'string' ? JSON.parse(company.special_clients) : company.special_clients)
          : [];

        this.dropdowns.users = data.users || [];
        this.dropdowns.clients = (data.clients || []).filter((c: any) => specialClientIds.some((id: any) => id == c.id));
        this.dropdowns.products = data.products || [];
        this.dropdowns.offices = data.offices || [];
        this.filteredProducts = [];

        if (this.id == 0) {
          const currentUserId = this.authenticationService.currentUserValue.id
          const userId = this.dropdowns.users.find((user: { id: number }) => user.id == currentUserId)
            ? currentUserId
            : this.dropdowns.users[0]?.id;

          this.saveForm.get('user_id')?.setValue(userId);
          this.saveForm.get('office_id')?.setValue(this.dropdowns.offices[0]?.id ?? null);
        }

        this.cdr.detectChanges();
      }
    });
  }

  filterProducts(query: string) {
    if (!query) {
      this.filteredProducts = [];
      return;
    }

    this.openProductSearch();

    const q = query.toLowerCase();
    this.filteredProducts = this.dropdowns.products.filter((p: any) =>
      p.name.toLowerCase().includes(q) ||
      p.id.toString().includes(q) ||
      (p.code && p.code.toLowerCase().includes(q))
    );
  }

  get catalogProducts(): any[] {
    return [...this.dropdowns.products].sort((a: any, b: any) => a.name.localeCompare(b.name));
  }

  openProductCatalog() {
    this.showProductCatalog = true;
  }

  closeProductCatalog() {
    this.showProductCatalog = false;
  }

  selectProductFromCatalog(product: any) {
    this.selectProductFromSearch(product, this.searchContainer.nativeElement);
    this.showProductCatalog = false;
  }

  addFirstProduct(inputElement: HTMLInputElement) {
    if (this.filteredProducts.length > 0) {
      const firstProduct = this.filteredProducts[0];
      this.selectProductFromSearch(firstProduct, inputElement);
    }
  }

  selectProductFromSearch(product: any, inputElement: HTMLInputElement) {
    const priceWithTariff = this.getPrice(product);

    this.addItem({
      id: null,
      product_id: product.id,
      name: product.name,
      price: priceWithTariff,
      quantity: 1,
      discount: 0,
      special_measures: [],
      tax: this.getProductTaxQuotient(product),
      apply_tax: 1,
    });

    inputElement.value = '';
    inputElement.focus()
    this.showResults = false;
    this.filteredProducts = [];
  }

  addItem(itemData: any = null) {
    const itemGroup = new FormGroup({
      id: new FormControl(itemData?.id || null),
      product_id: new FormControl(itemData?.product_id || null, [Validators.required]),
      name: new FormControl(itemData?.name || 'Nuevo artículo'),
      quantity: new FormControl(itemData?.quantity || 1, [Validators.required, Validators.min(1)]),
      discount: new FormControl(itemData?.discount || 0),
      price: new FormControl(itemData?.price || 0),
      special_measures: new FormControl(itemData?.special_measures || null),
      tax: new FormControl(itemData?.tax ?? null),
      apply_tax: new FormControl(itemData?.apply_tax ?? 1),
    });
    this.items.push(itemGroup);
  }

  removeItem(index: number) {
    this.items.removeAt(index);
    this.calculateTicket();
  }

  toggleEditMeasures(specialMeasuresObject: any = '', specialMeasures: string = '') {
    if (specialMeasures) {
      if (typeof specialMeasures == 'string')
        this.specialMeasures = JSON.parse(specialMeasures)
    }
    this.idEditMeasures = 0
    this.editMeasures = !this.editMeasures
  }

  openMeasures(index: number, currentSavedMeasures: any, productConfig: string) {
    this.idEditMeasures = index;

    this.specialMeasures = (currentSavedMeasures && Array.isArray(currentSavedMeasures) && currentSavedMeasures.length > 0)
      ? currentSavedMeasures
      : JSON.parse(productConfig);

    this.editMeasures = true;
  }

  acceptMeasures(item: { totalVolume: string, details: any }) {
    const product = this.items.controls[this.idEditMeasures]
    product.get('special_measures')?.setValue(item.details)
    product.get('quantity')?.setValue(item.totalVolume)

    this.toggleEditMeasures()
    this.cdr.detectChanges()
  }

  getItemQuotient(item: any): number {
    const product_id = item.get('product_id')?.value;
    const product = this.dropdowns.products.find((p: any) => p.id == product_id);
    const taxConfig = this.dropdowns.tax_config.find((t: any) => t.id == product?.tax_type);
    return taxConfig && taxConfig.quotient ? Number(taxConfig.quotient) : 21;
  }

  getAndCalculateWithOutTax(item: any) {
    const quantity = item.get('quantity')?.value || 0
    const price = item.get('price')?.value || 0
    const discount = (100 - (item.get('discount')?.value || 0)) / 100

    const lineTotal = quantity * price * discount;

    if (this.isTaxIncluded) {
      const quotient = this.getItemQuotient(item);
      return (lineTotal / ((quotient + 100) / 100)).toFixed(2)
    }

    return lineTotal.toFixed(2)
  }

  getAndCalculateWithTax(item: any) {
    const quantity = item.get('quantity')?.value || 0
    const price = item.get('price')?.value || 0
    const discount = (100 - (item.get('discount')?.value || 0)) / 100

    const lineTotal = quantity * price * discount;

    if (this.isTaxIncluded) {
      return lineTotal.toFixed(2)
    }

    const quotient = this.getItemQuotient(item);
    return (lineTotal * ((quotient + 100) / 100)).toFixed(2)
  }

  calculateTicket() {
    let sub = 0;
    let tot = 0;

    this.items.controls.forEach(c => {
      sub += Number(this.getAndCalculateWithOutTax(c));
      tot += Number(this.getAndCalculateWithTax(c));
    });

    const disc = this.saveForm.get('discount')?.value || 0;
    const isAbsolute = Number(this.saveForm.get('discount_absolute_bool')?.value) === 1;

    const lastSub = sub;

    if (isAbsolute) {
      sub -= disc;
    } else {
      sub *= (1 - (disc / 100));
    }

    tot = lastSub > 0 ? sub * tot / lastSub : 0;

    if (isNaN(tot)) {
      tot = 0;
    }

    this.saveForm.get('subtotal')?.setValue(sub.toFixed(2), { emitEvent: false });
    this.saveForm.get('total')?.setValue(tot.toFixed(2), { emitEvent: false });
  }

  showOfficeName() {
    return this.dropdowns.offices.find((o: any) => o.id == this.saveForm.get('office_id')?.value)?.name;
  }

  showIbanLabel() {
    const val = this.saveForm.get('iban')?.value;
    const found = this.dropdowns.ibans.find((i: any) => i.iban === val);
    return found ? found.label : (val ? val : '—');
  }

  showSeller() {
    return this.dropdowns.users.find((u: any) => u.id === this.saveForm.get('user_id')?.value)?.name
  }

  getIdByCompany() {
    const officeId = this.saveForm.get('office_id')?.value;
    const params = officeId ? '?office_id=' + officeId : '';
    this.apiService.getPetition(Urls.urls.tickets + '/last-id-by-company', params).subscribe((data: any) => {
      this.saveForm.patchValue({ id_by_company: data.last_id_by_company + 1 });
      this.cdr.detectChanges();
    });
  }

  getById(id: number | string) {
    this.loading.set(true);
    this.apiService.getById(Urls.urls.tickets, id).subscribe({
      next: (data: any) => {
        const ticket = data.ticket || {};

        this.saveForm.patchValue({
          id_by_company: ticket.id_by_company ?? null,
          year: ticket.year ?? new Date().getFullYear(),
          date: ticket.date ? ticket.date.split(' ')[0] : (ticket.date || new Date().toISOString().split('T')[0]),
          payment_method: ticket.payment_method ?? null,
          tax_included: ticket.tax_included ?? 1,
          user_id: ticket.user_id ?? this.authenticationService.currentUserValue.id,
          discount_absolute_bool: ticket.discount_absolute_bool ?? 0,
          discount: ticket.discount ?? 0,
          notes: ticket.notes ?? null,
          client_id: ticket.client_id ?? null,
          subtotal: (ticket.subtotal !== undefined && ticket.subtotal !== null) ? (Number(ticket.subtotal) / 100).toFixed(2) : '0.00',
          total: (ticket.total !== undefined && ticket.total !== null) ? (Number(ticket.total) / 100).toFixed(2) : '0.00',
          office_id: ticket.office_id ?? null,
          iban: ticket.iban ?? null,
          document_ref: ticket.document_ref ?? null,
        });

        while (this.mixPay.length) {
          this.mixPay.removeAt(0);
        }

        const mixPay = ticket.mix_pay
          ? (typeof ticket.mix_pay === 'string' ? JSON.parse(ticket.mix_pay) : ticket.mix_pay)
          : [];

        mixPay.forEach((m: any) => {
          this.addMixPay({ type: m.type, quantity: Number(m.quantity) / 100 });
        });

        const details = data.ticket_details || [];
        while (this.items.length) {
          this.items.removeAt(0);
        }

        details.forEach((d: any) => {
          const pivot = d.pivot || {};
          this.items.push(new FormGroup({
            id: new FormControl(pivot.id || null),
            product_id: new FormControl(d.id ?? null, [Validators.required]),
            name: new FormControl(pivot.personalized_desc || d.name || 'Nuevo artículo'),
            quantity: new FormControl(pivot.quantity ?? 1, [Validators.required, Validators.min(1)]),
            discount: new FormControl(pivot.discount ?? 0),
            price: new FormControl((pivot.price !== undefined && pivot.price !== null) ? (Number(pivot.price) / 100) : 0),
            special_measures: new FormControl(d.special_measures ?? null),
            tax: new FormControl(pivot.tax ?? this.getProductTaxQuotient(d)),
            apply_tax: new FormControl(pivot.apply_tax ? 1 : 0),
          }));
        });

        this.loading.set(false);
        this.cdr.detectChanges();
      },
      error: (error: any) => {
        console.error('ERROR al obtener el ticket. ' + error);
        this.loading.set(false);
      },
      complete: () => {
        this.loading.set(false);
      },
    });
  }

  saveEntity(printGift: boolean = false) {
    if (this.saveForm.valid) {
      if (!this.documentDeviceMonitor.canGenerateOffline()) {
        Utils.notification.alert('Sin conexión: este ordenador no está autorizado para generar tickets sin internet.');
        return;
      }

      this.saving.set(true);
      const payload = {
        ...this.saveForm.value,
        status: null,
      };

      payload.subtotal = Math.abs(payload.subtotal * 100)
      payload.total = Math.abs(payload.total * 100)

      payload.items = payload.items.map((item: any) => ({
        ...item,
        price: Math.abs(item.price * 100),
        special_measures: item.special_measures || null,
        personalized_desc: this.personalizedDesc(item),
      }));

      payload.mix_pay = payload.payment_method === 'mixto'
        ? payload.mix_pay.map((m: any) => ({ type: m.type, quantity: Math.abs(m.quantity * 100) }))
        : null;

      this.apiService.save(Urls.urls.tickets, payload, this.id).subscribe({
        next: (entity) => {
          Utils.notification.success("Ticket guardado correctamente")
          DocumentIdCache.record('tickets', null, entity.ticket.id, entity.ticket.id_by_company);
          if (printGift) {
            this.printGiftTicket(entity.ticket.id);
            this.stayInEdit(entity.ticket.id)
          } else {
            this.refresh(entity.ticket.id)
          }
        },
        error: (error: any) => {
          this.saving.set(false)
          Utils.notification.alert(Utils.extractErrorMessage(error) || "Error al guardar el ticket")
          this.cdr.detectChanges()
        },
        complete: () => {
          this.saving.set(false)
        }
      });
    }
  }

  /** Parte del ticket que se cobra en efectivo (todo si es efectivo, solo esa parte si es mixto). */
  get cashDue(): number {
    const method = this.saveForm.get('payment_method')?.value;
    if (method === 'efectivo') {
      return Number(this.saveForm.get('total')?.value) || 0;
    }
    if (method === 'mixto') {
      return this.mixPay.value
        .filter((m: any) => m.type === 'efectivo')
        .reduce((sum: number, m: any) => sum + (Number(m.quantity) || 0), 0);
    }
    return 0;
  }

  /**
   * Paso opcional antes de saveEntity(): si hay algo que cobrar en efectivo abre la calculadora de cambio,
   * que llama a saveEntity() al confirmar. Para guardar sin calculadora, los botones llaman a saveEntity() directamente.
   */
  finalize(printGift: boolean = false) {
    if (this.saveForm.invalid) return;

    if (this.cashDue > 0) {
      this.pendingChangePrintGift = printGift;
      return;
    }

    this.saveEntity(printGift);
  }

  closeChangeCalculator() {
    this.pendingChangePrintGift = null;
  }

  acceptChangeCalculator() {
    const printGift = !!this.pendingChangePrintGift;
    this.pendingChangePrintGift = null;
    this.saveEntity(printGift);
  }

  printGiftTicket(id: number) {
    const iframe = document.createElement('iframe');
    iframe.style.position = 'fixed';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = 'none';
    iframe.src = `/ticket/print-gift/${id}`;

    iframe.addEventListener('load', () => {
      iframe.contentWindow?.addEventListener('afterprint', () => iframe.remove());
      setTimeout(() => iframe.remove(), 15000);
    });

    document.body.appendChild(iframe);
  }

  stayInEdit(id: number) {
    this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
      this.router.navigate(['/tickets/edit', id]);
    });
  }

  refresh(id: number) {
    this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
      this.router.navigate(['/tickets']);
    });
  }

  changeClient() {
    const id = this.saveForm.get('client_id')?.value;
    const client = this.dropdowns.clients.find((c: any) => c.id == id);
    if (client) {
      if (client.discount) {
        this.saveForm.get('discount')?.setValue(client.discount)
        this.saveForm.get('discount_absolute_bool')?.setValue(0) // percentage
      }

      this.searchContainer.nativeElement.focus()
    }
  }

  // Persist a manually-edited line name as personalized_desc; if it still
  // matches the underlying product's real name, don't store an override.
  private personalizedDesc(item: any): string | null {
    const originalName = (this.getProductInfo(item.product_id)?.name || '').trim();
    const currentName = (item.name || '').trim();
    return (currentName && currentName !== originalName) ? currentName : null;
  }

  getProductInfo(productId: any) {
    if (!productId || !this.dropdowns.products) return null;
    return this.dropdowns.products.find((p: any) => p.id == productId);
  }

  getProductTaxQuotient(product: any): number | null {
    if (!product || !this.dropdowns.tax_config) return null;
    const taxConfig = this.dropdowns.tax_config.find((t: any) => t.id == product.tax_type);
    return taxConfig?.quotient ? Number(taxConfig.quotient) : null;
  }

  onDiscountTab(event: KeyboardEvent, isLast: boolean, searchInput: HTMLInputElement): void {
    if (isLast) {
      event.preventDefault();
      searchInput.focus();
    }
  }

  onSearchProductTab(event: Event, isLast: boolean): void {
    if (isLast) {
      this.showResults = false;
    }
  }
}
